import React, { useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { UploadSimple, DownloadSimple, CaretDown, Users as UsersIcon } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

/**
 * Bulk user import button + template downloader. RBAC is enforced server-side
 * (institution_admin / super_admin) — we still show the same UI to any caller
 * because we only render it where already gated by role.
 */
export default function ImportUsersButton({ onImported }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/users/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const created = res.data.created ?? 0;
      const skipped = (res.data.skipped || []).length;
      const total = res.data.total_rows ?? created + skipped;
      const msg = `Created ${created} of ${total} users` + (skipped ? ` · ${skipped} skipped` : "");
      if (skipped > 0) toast.warning(msg + ": " + (res.data.skipped[0]?.reason || "see details"));
      else toast.success(msg);
      onImported && onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const downloadTemplate = async (fmt) => {
    try {
      const res = await api.get(`/users/template?format=${fmt}`, { responseType: "blob" });
      const ext = fmt === "excel" ? "xlsx" : fmt;
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `users_template.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Template downloaded (${ext.toUpperCase()})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Template download failed");
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".json,.csv,.xlsx,.xls"
        onChange={handleFile}
        className="hidden"
        data-testid="users-import-file-input"
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            disabled={busy}
            className="border border-zinc-300 px-4 py-2.5 text-sm font-medium hover:bg-zinc-50 inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="users-import-btn"
          >
            <UsersIcon size={14} />
            {busy ? "Importing…" : "Bulk import"}
            <CaretDown size={12} weight="bold" className="ml-1" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Onboard users</DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-2 text-sm cursor-pointer"
            data-testid="users-import-open-file-picker"
          >
            <UploadSimple size={14} /> Upload file (.csv/.xlsx/.json)
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Download template</DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => downloadTemplate("xlsx")}
            className="flex items-center gap-2 text-sm cursor-pointer"
            data-testid="users-template-download-xlsx"
          >
            <DownloadSimple size={14} className="text-emerald-600" /> Excel (.xlsx)
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => downloadTemplate("csv")}
            className="flex items-center gap-2 text-sm cursor-pointer"
            data-testid="users-template-download-csv"
          >
            <DownloadSimple size={14} className="text-blue-600" /> CSV (.csv)
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => downloadTemplate("json")}
            className="flex items-center gap-2 text-sm cursor-pointer"
            data-testid="users-template-download-json"
          >
            <DownloadSimple size={14} className="text-amber-600" /> JSON (.json)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
